import { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FileText, Link as LinkIcon, ExternalLink, Type } from 'lucide-react';
import { Button, Sheet } from '@ai-tutor/ui';
import { useMediaQuery } from '@/hooks/ui/useMediaQuery';
import { type CitedByte } from '@/lib/api-client';
import { apiClient } from '@/lib/api-client';
import { notify } from '@/lib/notify';
import { cn } from '@/lib/utils';

interface CitationChipProps {
  index: number;
  citation: CitedByte;
}

const TYPE_ICON: Record<CitedByte['source_type'], typeof FileText> = {
  file: FileText,
  link: LinkIcon,
  text: Type,
};

const HOVER_OPEN_DELAY = 120;
const HOVER_CLOSE_DELAY = 150;
const CARD_WIDTH = 320;
const VIEWPORT_PADDING = 8;

/**
 * Inline citation pointing back to an external source. Reading-flow neutral
 * (slate `¹`-style superscript), reveals a hover card with the snippet and
 * an "Open source" link, opens the source in a new tab on click.
 *
 * Built without Radix HoverCard - the UI package doesn't bundle one and a
 * floating-portal popover with hover-intent timers covers what we need.
 *
 * On a phone or any touch screen there is no hover, so the card was never
 * seen and a tap went straight out to a new tab. There the chip is a 12px
 * pill and a tap opens `CitationSheet` - the source, the quoted snippet and
 * a full-width "Open source" - which is where a reader asks "where is this
 * from?" before leaving the lesson. It stays drawn small: it sits inside a
 * sentence, the case WCAG 2.5.8 exempts from target size, and a 44px box
 * would cover the lines above and below (`data-inline-target`).
 */
export function CitationChip({ index, citation }: CitationChipProps) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const openTimer = useRef<number | null>(null);
  const closeTimer = useRef<number | null>(null);
  const [open, setOpen] = useState(false);
  const touch = useMediaQuery('(max-width: 639.98px), (pointer: coarse)');
  const [position, setPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);

  const Icon = TYPE_ICON[citation.source_type] ?? FileText;
  // A file opens through the API client; anything with a url opens it - a
  // link, and the textbook sections and web pages found for a topic (text
  // sources the student never sees in their Sources: the citation is their
  // way to the original). Pasted text has no destination.
  const canOpen =
    (citation.source_type === 'file' && !!citation.file_key) ||
    (citation.source_type !== 'file' && !!citation.url);

  const scheduleOpen = () => {
    if (closeTimer.current) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    if (openTimer.current) return;
    openTimer.current = window.setTimeout(() => {
      setOpen(true);
      openTimer.current = null;
    }, HOVER_OPEN_DELAY);
  };

  const scheduleClose = () => {
    if (openTimer.current) {
      window.clearTimeout(openTimer.current);
      openTimer.current = null;
    }
    if (closeTimer.current) return;
    closeTimer.current = window.setTimeout(() => {
      setOpen(false);
      closeTimer.current = null;
    }, HOVER_CLOSE_DELAY);
  };

  // Position the hover card above the chip, viewport-clamped.
  useLayoutEffect(() => {
    if (touch || !open || !triggerRef.current || !cardRef.current) {
      setPosition(null);
      return;
    }
    const triggerRect = triggerRef.current.getBoundingClientRect();
    const cardRect = cardRef.current.getBoundingClientRect();

    let top = triggerRect.top - cardRect.height - 8;
    if (top < VIEWPORT_PADDING) {
      top = triggerRect.bottom + 8;
    }
    let left = triggerRect.left + triggerRect.width / 2 - cardRect.width / 2;
    const maxLeft = window.innerWidth - cardRect.width - VIEWPORT_PADDING;
    if (left < VIEWPORT_PADDING) left = VIEWPORT_PADDING;
    if (left > maxLeft) left = maxLeft;

    setPosition({ top: top + window.scrollY, left });
  }, [open, touch]);

  const handleClick = async () => {
    if (citation.source_type !== 'file' && citation.url) {
      window.open(citation.url, '_blank', 'noopener,noreferrer');
      return;
    }
    if (citation.source_type === 'file' && citation.file_key) {
      try {
        // Use the existing presigned-URL endpoint. The api-client's typed
        // surface doesn't expose this one, so reach the underlying client.
        const { url } = await apiClient.get<{
          url: string;
          expires_in: number;
        }>(`/sources/${citation.source_id}/file`);
        window.open(url, '_blank', 'noopener,noreferrer');
      } catch (err: unknown) {
        notify.error('Failed to open source');
        console.error('Failed to fetch presigned URL', err);
      }
      return;
    }
    // Text sources - no destination.
  };

  return (
    <>
      <button
        ref={triggerRef}
        type='button'
        data-inline-target=''
        {...(touch
          ? { onClick: () => setOpen(true), 'aria-haspopup': 'dialog' as const }
          : {
              onMouseEnter: scheduleOpen,
              onMouseLeave: scheduleClose,
              onFocus: scheduleOpen,
              onBlur: scheduleClose,
              onClick: handleClick,
            })}
        // Render as superscript via vertical-align + small font. Recede in
        // normal reading; bump contrast on hover.
        className={cn(
          'mx-px inline align-super text-[0.65em] font-medium text-slate-500',
          'transition-colors hover:text-brand-600',
          'dark:text-muted-foreground dark:hover:text-primary',
          canOpen ? 'cursor-pointer' : 'cursor-default',
          'rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400',
          touch &&
            'mx-0.5 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-muted px-1 align-[0.35em] font-mono text-xs leading-none text-muted-foreground'
        )}
        aria-label={`Citation ${index} from ${citation.source_name}`}
      >
        {index}
      </button>
      {touch && (
        <CitationSheet
          index={index}
          citation={citation}
          isOpen={open}
          canOpen={canOpen}
          onClose={() => setOpen(false)}
          onOpenSource={handleClick}
        />
      )}
      {open &&
        !touch &&
        createPortal(
          <div
            ref={cardRef}
            onMouseEnter={scheduleOpen}
            onMouseLeave={scheduleClose}
            style={
              position
                ? {
                    position: 'absolute',
                    top: position.top,
                    left: position.left,
                    width: CARD_WIDTH,
                  }
                : {
                    position: 'absolute',
                    top: -9999,
                    left: -9999,
                    width: CARD_WIDTH,
                  }
            }
            className='z-50 rounded-md border border-border bg-popover p-3 text-xs shadow-md'
          >
            <div className='mb-1.5 flex items-center gap-1.5 text-foreground/70'>
              <Icon className='h-3.5 w-3.5 flex-shrink-0' />
              <span className='truncate font-medium'>
                {citation.source_name}
              </span>
            </div>
            <p className='mb-2 leading-relaxed text-foreground/80'>
              {citation.snippet}
            </p>
            {canOpen ? (
              <button
                type='button'
                onClick={handleClick}
                className='inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline'
              >
                Open source
                <ExternalLink className='h-3 w-3' />
              </button>
            ) : (
              <span className='inline-flex items-center gap-1 text-xs text-muted-foreground'>
                No external link
              </span>
            )}
          </div>,
          document.body
        )}
    </>
  );
}

/** The touch form of the hover card: a bottom sheet, opened by a tap. */
function CitationSheet({
  index,
  citation,
  isOpen,
  canOpen,
  onClose,
  onOpenSource,
}: {
  index: number;
  citation: CitedByte;
  isOpen: boolean;
  canOpen: boolean;
  onClose: () => void;
  onOpenSource: () => void;
}) {
  const Icon = TYPE_ICON[citation.source_type] ?? FileText;
  return (
    <Sheet isOpen={isOpen} onClose={onClose} title={`Source ${index}`}>
      <div className='space-y-3 px-4 pb-4'>
        <p className='flex items-center gap-2 text-sm font-medium text-foreground'>
          <Icon
            className='h-4 w-4 flex-shrink-0 text-muted-foreground'
            aria-hidden='true'
          />
          <span className='min-w-0 truncate'>{citation.source_name}</span>
        </p>
        {citation.snippet && (
          <blockquote className='border-l-2 border-border pl-3 text-sm leading-relaxed text-foreground/80'>
            {citation.snippet}
          </blockquote>
        )}
        {canOpen ? (
          <Button className='h-11 w-full' onClick={onOpenSource}>
            Open source
            <ExternalLink aria-hidden='true' />
          </Button>
        ) : (
          <p className='text-xs text-muted-foreground'>
            A pasted-text source has no page to open.
          </p>
        )}
      </div>
    </Sheet>
  );
}

export interface CitationMaps {
  byId: Map<string, CitedByte>;
  index: Map<string, number>;
}

/** Markdown `a`: `cite://` links become CitationChips, the rest stay links. */
export function citationAwareLink(maps: { current: CitationMaps }) {
  return function CitationAwareLink({
    href,
    children,
    ...rest
  }: {
    href?: string;
    children?: React.ReactNode;
  }) {
    if (href?.startsWith('cite://')) {
      const byteId = href.slice('cite://'.length);
      const citation = maps.current.byId.get(byteId);
      const index = maps.current.index.get(byteId);
      if (!citation || index === undefined) return null;
      return <CitationChip index={index} citation={citation} />;
    }
    return (
      <a href={href} {...rest}>
        {children}
      </a>
    );
  };
}
